'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription as AlertDialogDescriptionRoot,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import * as React from 'react';

type ConfirmOptions = {
  title?: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';

  waitSeconds?: number;
  requireCheckbox?: boolean;
  checkboxLabel?: React.ReactNode;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmationDialogContext = React.createContext<ConfirmFn | null>(null);

export function useConfirmationDialog(): ConfirmFn {
  const ctx = React.useContext(ConfirmationDialogContext);
  if (!ctx) {
    throw new Error(
      'useConfirmationDialog must be used within <ConfirmationDialogProvider>'
    );
  }
  return ctx;
}

export function ConfirmationDialogDescription({
  description,
}: {
  description?: React.ReactNode;
}) {
  if (!description) return null;

  return (
    <AlertDialogDescriptionRoot asChild>
      <div>{description}</div>
    </AlertDialogDescriptionRoot>
  );
}

export function ConfirmationDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<ConfirmOptions>({});

  const resolverRef = React.useRef<((value: boolean) => void) | null>(null);

  const [remainingSeconds, setRemainingSeconds] = React.useState<number | null>(
    null
  );
  const [checkboxChecked, setCheckboxChecked] = React.useState(false);

  const confirm: ConfirmFn = React.useCallback(opts => {
    setOptions(opts);
    setOpen(true);

    setCheckboxChecked(false);
    if (opts.waitSeconds && opts.waitSeconds > 0) {
      setRemainingSeconds(opts.waitSeconds);
    } else {
      setRemainingSeconds(null);
    }

    return new Promise<boolean>(resolve => {
      resolverRef.current = resolve;
    });
  }, []);

  const resolveAndClose = (value: boolean) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setOpen(false);
    // setRemainingSeconds(null);
    // setCheckboxChecked(false);
  };

  React.useEffect(() => {
    if (!open) return;

    if (!options.waitSeconds || options.waitSeconds <= 0) return;
    if (remainingSeconds === null) return;

    if (remainingSeconds <= 0) return;

    const id = window.setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev === null) return prev;
        if (prev <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [open, options.waitSeconds, remainingSeconds]);

  const {
    title = 'Are you sure?',
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'default',
    waitSeconds,
    requireCheckbox,
    checkboxLabel = 'I understand the consequences.',
  } = options;

  const isCountingDown =
    typeof remainingSeconds === 'number' && remainingSeconds > 0;

  const confirmDisabled =
    (waitSeconds && waitSeconds > 0 && isCountingDown) ||
    (requireCheckbox && !checkboxChecked);

  const renderConfirmLabel = () => {
    if (waitSeconds && waitSeconds > 0 && isCountingDown) {
      return `${confirmLabel} (${remainingSeconds})`;
    }
    return confirmLabel;
  };

  return (
    <ConfirmationDialogContext.Provider value={confirm}>
      {children}

      <AlertDialog
        open={open}
        onOpenChange={next => {
          if (!next) {
            resolveAndClose(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <ConfirmationDialogDescription description={description} />
          </AlertDialogHeader>

          {requireCheckbox && (
            <div className="mt-4 flex items-start space-x-2">
              <Checkbox
                id="confirm-checkbox"
                checked={checkboxChecked}
                onCheckedChange={checked =>
                  setCheckboxChecked(checked === true)
                }
              />
              <label
                htmlFor="confirm-checkbox"
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {checkboxLabel}
              </label>
            </div>
          )}

          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={() => resolveAndClose(false)}>
              {cancelLabel}
            </AlertDialogCancel>

            <AlertDialogAction
              disabled={confirmDisabled}
              className={cn(
                variant === 'destructive' &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                confirmDisabled && 'cursor-not-allowed opacity-70'
              )}
              onClick={() => resolveAndClose(true)}
            >
              {renderConfirmLabel()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationDialogContext.Provider>
  );
}
